import React, { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Clock, MapPin, Calendar as CalendarIcon, Users2 } from "lucide-react";

export default function StudentBook() {
  const { user } = useAuth();
  const [slots, setSlots] = useState([]);        // computed from availability rules
  const [events, setEvents] = useState([]);      // clinics/mixers/open_play from /lessons/available
  const [tab, setTab] = useState("lessons");
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [booking, setBooking] = useState(false);

  const load = async () => {
    try {
      const [sl, ev] = await Promise.all([
        api.get("/availability/slots", { params: { days: 28 } }),
        api.get("/lessons/available"),
      ]);
      setSlots(sl.data || []);
      setEvents(ev.data || []);
    } catch (_) {}
  };
  useEffect(() => { load(); }, []);

  // Group slots by day
  const slotsByDay = useMemo(() => {
    const groups = {};
    for (const s of slots) {
      const key = new Date(s.start).toDateString();
      if (!groups[key]) groups[key] = [];
      groups[key].push(s);
    }
    return groups;
  }, [slots]);

  const days = Object.keys(slotsByDay);
  const activeDay = selectedDate && slotsByDay[selectedDate] ? selectedDate : days[0];
  const activeSlots = activeDay ? slotsByDay[activeDay] : [];

  const confirmBook = async () => {
    if (!selectedSlot) return;
    setBooking(true);
    try {
      await api.post("/availability/book", {
        coach_id: selectedSlot.coach_id,
        start: selectedSlot.start,
        duration_mins: selectedSlot.duration_mins,
        location: selectedSlot.location,
        price: selectedSlot.price,
        title: "Private lesson",
      });
      toast.success("Booked! Check your email.");
      setSelectedSlot(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not book");
    }
    setBooking(false);
  };

  const bookEvent = async (id) => {
    try {
      const res = await api.post(`/lessons/${id}/book`);
      if (res.data?.waitlisted) {
        toast.success(`You're on the waitlist (position ${res.data.waitlist_position}). We'll let you know if a spot opens.`);
      } else {
        toast.success("Booked!");
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not book");
    }
  };

  return (
    <div className="space-y-6" data-testid="student-book-page">
      <div>
        <div className="text-[10px] tracking-[0.3em] uppercase text-[#38BDF8] font-bold">Book with your coach</div>
        <h2 className="font-display text-4xl font-black uppercase tracking-tighter mt-2">Find a time</h2>
        <p className="text-sm text-[#A3A3A3] mt-2">
          Private lessons show your coach's available windows — pick a day, pick a time. Clinics & mixers are listed separately.
        </p>
      </div>

      {/* Tab switch */}
      <div className="inline-grid grid-cols-2 border border-white/10" data-testid="book-tabs">
        <button
          onClick={() => setTab("lessons")}
          className={`px-8 py-3 text-xs uppercase tracking-widest font-bold transition-colors ${tab === "lessons" ? "bg-[#38BDF8] text-black" : "text-white hover:bg-white/5"}`}
          data-testid="book-tab-lessons"
        >
          Private Lessons
        </button>
        <button
          onClick={() => setTab("events")}
          className={`px-8 py-3 text-xs uppercase tracking-widest font-bold transition-colors ${tab === "events" ? "bg-[#38BDF8] text-black" : "text-white hover:bg-white/5"}`}
          data-testid="book-tab-events"
        >
          Clinics & Mixers {events.length > 0 && <span className="ml-2 text-[10px] opacity-70">({events.length})</span>}
        </button>
      </div>

      {tab === "lessons" ? (
        days.length === 0 ? (
          <div className="border border-dashed border-white/10 p-12 text-center text-[#A3A3A3]" data-testid="book-no-slots">
            Your coach hasn't published availability yet. Send them a message!
          </div>
        ) : (
          <div className="grid lg:grid-cols-[280px_1fr] gap-6">
            {/* Day picker */}
            <div className="card-surface overflow-hidden">
              <div className="p-4 border-b border-white/10 text-[10px] tracking-widest uppercase text-[#38BDF8] font-bold">
                Pick a day
              </div>
              <div className="max-h-[60vh] overflow-auto">
                {days.map((d) => {
                  const date = new Date(d);
                  const active = d === activeDay;
                  return (
                    <button
                      key={d}
                      onClick={() => setSelectedDate(d)}
                      className={`w-full text-left px-4 py-3 border-b border-white/5 flex items-baseline justify-between transition-colors ${active ? "bg-[#38BDF8]/10 border-l-2 border-l-[#38BDF8]" : "hover:bg-white/[0.03]"}`}
                      data-testid={`book-day-${date.toISOString().slice(0, 10)}`}
                    >
                      <div>
                        <div className="text-[10px] uppercase tracking-widest text-[#A3A3A3]">{date.toLocaleDateString([], { weekday: "short" })}</div>
                        <div className="font-display text-xl font-bold uppercase">{date.toLocaleDateString([], { month: "short", day: "numeric" })}</div>
                      </div>
                      <div className="text-xs text-[#A3A3A3]">{slotsByDay[d].length} slot{slotsByDay[d].length > 1 ? "s" : ""}</div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Slot grid */}
            <div className="card-surface p-6">
              {activeDay ? (
                <>
                  <div className="text-[10px] tracking-widest uppercase text-[#38BDF8] font-bold mb-1">{new Date(activeDay).toDateString()}</div>
                  <div className="font-display text-3xl font-black uppercase tracking-tight mb-5">Pick a time</div>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                    {activeSlots.map((s, i) => (
                      <button
                        key={i}
                        onClick={() => setSelectedSlot(s)}
                        className="border border-white/15 px-3 py-4 text-sm font-mono font-bold hover:border-[#38BDF8] hover:bg-[#38BDF8]/10 transition-colors"
                        data-testid={`book-slot-${i}`}
                      >
                        {new Date(s.start).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}
                      </button>
                    ))}
                  </div>
                </>
              ) : (
                <div className="text-[#A3A3A3] text-sm">Pick a day on the left.</div>
              )}
            </div>
          </div>
        )
      ) : (
        events.length === 0 ? (
          <div className="border border-dashed border-white/10 p-12 text-center text-[#A3A3A3]" data-testid="book-no-events">
            No clinics or mixers open right now.
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {events.map((l) => (
              <div key={l.id} className="card-surface p-6 flex flex-col" data-testid={`event-${l.id}`}>
                <div className="text-[10px] uppercase tracking-widest text-[#38BDF8] font-bold">
                  {new Date(l.scheduled_at).toLocaleString([], { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                </div>
                <div className="font-display text-2xl font-black uppercase tracking-tight mt-2">{l.title}</div>
                <div className="text-xs text-[#A3A3A3] mt-2 flex flex-wrap items-center gap-2">
                  <Users2 size={12} /> {(l.booked_students || []).length}/{l.max_students} booked · {l.type.replace("_", " ")}
                  {l.full && <span className="text-[#F59E0B] font-bold uppercase text-[10px] tracking-widest">Full</span>}
                  {l.location && <><MapPin size={12} /> {l.location}</>}
                  {l.price ? `· $${l.price}` : ""}
                </div>
                <button
                  onClick={() => bookEvent(l.id)}
                  className="btn-primary mt-5 w-full py-3 text-xs"
                  data-testid={`event-book-${l.id}`}
                >
                  {l.full ? "Join waitlist" : "Book"}
                </button>
              </div>
            ))}
          </div>
        )
      )}

      {/* Confirm dialog */}
      <Dialog open={!!selectedSlot} onOpenChange={(v) => !v && setSelectedSlot(null)}>
        <DialogContent className="rounded-none bg-[#141414] border-white/10 max-w-md" data-testid="book-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-tight text-2xl">Confirm booking</DialogTitle>
          </DialogHeader>
          {selectedSlot && (
            <div className="space-y-4">
              <div className="border border-white/10 p-4 space-y-2 text-sm">
                <div className="flex items-center gap-2"><CalendarIcon size={14} className="text-[#38BDF8]" />{new Date(selectedSlot.start).toLocaleString([], { weekday: "long", month: "long", day: "numeric", hour: "numeric", minute: "2-digit" })}</div>
                <div className="flex items-center gap-2"><Clock size={14} className="text-[#38BDF8]" />{selectedSlot.duration_mins} minutes · Private lesson</div>
                {selectedSlot.location && <div className="flex items-center gap-2"><MapPin size={14} className="text-[#38BDF8]" />{selectedSlot.location}</div>}
                {selectedSlot.price > 0 && <div className="text-[#A3A3A3]">Price: ${selectedSlot.price}</div>}
              </div>
              <div className="flex gap-2">
                <button onClick={() => setSelectedSlot(null)} className="btn-secondary flex-1 py-3 text-sm" data-testid="book-cancel-button">
                  Cancel
                </button>
                <button onClick={confirmBook} disabled={booking} className="btn-primary flex-1 py-3 text-sm" data-testid="book-confirm-button">
                  {booking ? "Booking…" : "Confirm"}
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
